import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import WhatsAppWidget from '../../components/ui/WhatsAppWidget';
import NewsletterSubscription from '../../components/ui/NewsletterSubscription';
import ConsultationBooking from '../../components/ui/ConsultationBooking';
import ProjectCard from './components/ProjectCard';
import FilterPanel from './components/FilterPanel';
import ProjectModal from './components/ProjectModal';
import FeaturedCarousel from './components/FeaturedCarousel';
import ProjectStats from './components/ProjectStats';
import ClientLogos from './components/ClientLogos';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ProjectPortfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTech, setSelectedTech] = useState([]);
  const [sortBy, setSortBy] = useState('recent');
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredProjects, setFilteredProjects] = useState([]);

  const projects = [
  {
    id: 1,
    title: 'E-Commerce Platform Transformation',
    category: 'tech',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1eb7c1cb3-1764655134961.png",
    thumbnailAlt: 'Modern e-commerce dashboard interface with product listings, shopping cart, and payment gateway integration displayed on laptop screen',
    description: 'Complete digital transformation of traditional retail business into modern e-commerce platform with AI-powered recommendations',
    fullDescription: `Led the complete digital transformation of a traditional retail business into a cutting-edge e-commerce platform. The project involved migrating legacy systems, implementing AI-powered product recommendations, and creating a seamless omnichannel shopping experience.\n\nThe platform now handles over 10,000 daily transactions with 99.9% uptime, featuring real-time inventory management, automated order processing, and integrated payment gateways supporting multiple currencies across African markets.`,
    technologies: ['React', 'Node.js', 'MongoDB', 'AWS', 'Docker', 'GraphQL'],
    year: '2025',
    duration: '8 months',
    teamSize: '12 members',
    objectives: [
    'Migrate legacy systems to modern cloud infrastructure',
    'Implement AI-powered product recommendation engine',
    'Create seamless mobile and web shopping experience',
    'Integrate multiple payment gateways for African markets',
    'Achieve 99.9% platform uptime and reliability'],

    testimonial: {
      quote: 'Edrian transformed our business completely. The new platform increased our online sales by 300% in the first quarter alone.',
      clientName: 'Amaka Okonkwo',
      clientRole: 'CEO, RetailHub Nigeria',
      clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1a0ea65bb-1763296441079.png",
      clientImageAlt: 'Professional headshot of African woman with braided hair wearing elegant blue business suit and pearl necklace'
    },
    metrics: [
    { label: 'Sales Increase', value: '300%', percentage: 100 },
    { label: 'User Engagement', value: '85%', percentage: 85 },
    { label: 'Page Load Speed', value: '1.2s', percentage: 95 },
    { label: 'Customer Satisfaction', value: '4.8/5', percentage: 96 }],

    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com',
    featured: true
  },
  {
    id: 2,
    title: 'FinTech Mobile Banking App',
    category: 'tech',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1eff135a3-1766601351747.png",
    thumbnailAlt: 'Sleek mobile banking app interface showing account balance, transaction history, and quick transfer buttons on smartphone screen',
    description: 'Revolutionary mobile banking solution with biometric authentication and instant cross-border payments',
    fullDescription: `Developed a revolutionary mobile banking application that brings financial services to underserved communities across Africa. The app features biometric authentication, instant cross-border payments, and micro-lending capabilities.\n\nWith over 500,000 active users, the platform has processed over $50 million in transactions, providing accessible financial services to previously unbanked populations while maintaining bank-grade security standards.`,
    technologies: ['React Native', 'TypeScript', 'Firebase', 'Stripe', 'Plaid'],
    year: '2025',
    duration: '10 months',
    teamSize: '15 members',
    objectives: [
    'Create accessible banking for underserved communities',
    'Implement bank-grade security with biometric authentication',
    'Enable instant cross-border payment processing',
    'Develop micro-lending algorithm for credit scoring',
    'Achieve regulatory compliance across multiple countries'],

    testimonial: {
      quote: 'This app has revolutionized how we serve our customers. The user experience is exceptional and the security features are top-notch.',
      clientName: 'Kwame Mensah',
      clientRole: 'CTO, AfriBank Solutions',
      clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_18d854688-1763295573707.png",
      clientImageAlt: 'Professional portrait of African man with short hair wearing navy blue suit and red tie with confident smile'
    },
    metrics: [
    { label: 'Active Users', value: '500K+', percentage: 100 },
    { label: 'Transaction Volume', value: '$50M', percentage: 90 },
    { label: 'App Rating', value: '4.7/5', percentage: 94 },
    { label: 'Security Score', value: '99%', percentage: 99 }],

    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com',
    featured: true
  },
  {
    id: 3,
    title: 'Brand Identity & Digital Presence',
    category: 'creative',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_147e77f6b-1767084365685.png",
    thumbnailAlt: 'Creative brand identity design mockup showing logo variations, color palette, typography samples, and business card designs on wooden desk',
    description: 'Complete brand transformation including logo design, website development, and social media strategy',
    fullDescription: `Orchestrated a complete brand transformation for a growing startup, creating a cohesive visual identity that resonates with their target audience. The project encompassed logo design, brand guidelines, website development, and comprehensive social media strategy.\n\nThe new brand identity increased brand recognition by 250% and social media engagement by 400%, establishing the company as a thought leader in their industry.`,
    technologies: ['Figma', 'Adobe Creative Suite', 'React', 'Next.js', 'Tailwind CSS'],
    year: '2024',
    duration: '6 months',
    teamSize: '8 members',
    objectives: [
    'Create memorable and scalable brand identity',
    'Develop comprehensive brand guidelines',
    'Design and launch modern responsive website',
    'Establish strong social media presence',
    'Increase brand recognition and engagement'],

    testimonial: {
      quote: 'Edrian captured our vision perfectly. The new brand identity has transformed how customers perceive our company.',
      clientName: 'Sarah Johnson',
      clientRole: 'Founder, InnovateTech',
      clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1135cd9d1-1763298257986.png",
      clientImageAlt: 'Professional headshot of Caucasian woman with blonde hair in modern gray blazer with warm friendly expression'
    },
    metrics: [
    { label: 'Brand Recognition', value: '250%', percentage: 100 },
    { label: 'Social Engagement', value: '400%', percentage: 100 },
    { label: 'Website Traffic', value: '180%', percentage: 90 },
    { label: 'Lead Generation', value: '220%', percentage: 95 }],

    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com',
    featured: true
  },
  {
    id: 4,
    title: 'Healthcare Management System',
    category: 'tech',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1644aff77-1764659451775.png",
    thumbnailAlt: 'Modern healthcare dashboard showing patient records, appointment scheduling, and medical analytics on desktop computer screen',
    description: 'Comprehensive healthcare management platform with patient records, appointment scheduling, and telemedicine',
    fullDescription: `Built a comprehensive healthcare management system that digitizes patient records, streamlines appointment scheduling, and enables telemedicine consultations. The platform serves over 50 healthcare facilities across Nigeria and Cameroon.\n\nThe system has reduced administrative overhead by 60% while improving patient care quality through better data management and accessibility.`,
    technologies: ['Vue.js', 'Python', 'PostgreSQL', 'Redis', 'WebRTC'],
    year: '2024',
    duration: '12 months',
    teamSize: '18 members',
    objectives: [
    'Digitize patient records with HIPAA compliance',
    'Implement efficient appointment scheduling system',
    'Enable secure telemedicine consultations',
    'Create analytics dashboard for healthcare insights',
    'Ensure system scalability for multiple facilities'],

    testimonial: {
      quote: 'This system has revolutionized how we manage patient care. The efficiency gains are remarkable.',
      clientName: 'Dr. Chidi Okafor',
      clientRole: 'Medical Director, HealthFirst Clinics',
      clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1f147df5d-1763301649610.png",
      clientImageAlt: 'Professional portrait of African male doctor wearing white medical coat with stethoscope and warm reassuring smile'
    },
    metrics: [
    { label: 'Facilities Served', value: '50+', percentage: 100 },
    { label: 'Admin Efficiency', value: '60%', percentage: 60 },
    { label: 'Patient Satisfaction', value: '4.9/5', percentage: 98 },
    { label: 'System Uptime', value: '99.8%', percentage: 99 }],

    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com',
    featured: false
  },
  {
    id: 5,
    title: 'Business Consulting & Strategy',
    category: 'business',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1d277bb8f-1767097201638.png",
    thumbnailAlt: 'Business strategy session with charts, graphs, and financial documents spread across conference table with laptop and coffee',
    description: 'Strategic business consulting for startup growth and digital transformation initiatives',
    fullDescription: `Provided comprehensive business consulting services to help a struggling startup pivot their business model and achieve profitability. The engagement included market analysis, competitive positioning, operational optimization, and digital transformation strategy.\n\nThe company achieved profitability within 6 months and secured Series A funding of $2M, growing their team from 8 to 45 employees.`,
    technologies: ['Business Strategy', 'Market Analysis', 'Financial Modeling', 'Digital Transformation'],
    year: '2024',
    duration: '9 months',
    teamSize: '5 consultants',
    objectives: [
    'Conduct comprehensive market and competitive analysis',
    'Develop sustainable business model and revenue streams',
    'Optimize operational processes and reduce costs',
    'Create digital transformation roadmap',
    'Secure funding and scale operations'],

    testimonial: {
      quote: 'Edrian\'s strategic guidance was instrumental in turning our company around. We went from struggling to thriving.',
      clientName: 'Emmanuel Adeyemi',
      clientRole: 'CEO, TechStart Africa',
      clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1f95a7a07-1763293569074.png",
      clientImageAlt: 'Professional headshot of African entrepreneur in charcoal suit with confident expression and modern office background'
    },
    metrics: [
    { label: 'Revenue Growth', value: '350%', percentage: 100 },
    { label: 'Cost Reduction', value: '40%', percentage: 40 },
    { label: 'Funding Secured', value: '$2M', percentage: 100 },
    { label: 'Team Growth', value: '460%', percentage: 100 }],

    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com',
    featured: false
  },
  {
    id: 6,
    title: 'Educational Platform Development',
    category: 'tech',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_176c1c8b4-1764661436203.png",
    thumbnailAlt: 'Modern e-learning platform interface showing video lectures, course materials, and interactive quizzes on tablet device',
    description: 'Interactive e-learning platform with video courses, assessments, and progress tracking',
    fullDescription: `Developed a comprehensive e-learning platform that makes quality education accessible to students across Africa. The platform features interactive video courses, real-time assessments, progress tracking, and AI-powered personalized learning paths.\n\nWith over 100,000 enrolled students and 500+ courses, the platform has become a leading educational resource, helping students achieve their academic and professional goals.`,
    technologies: ['React', 'Node.js', 'MongoDB', 'AWS', 'WebRTC', 'TensorFlow'],
    year: '2024',
    duration: '11 months',
    teamSize: '14 members',
    objectives: [
    'Create accessible quality education platform',
    'Implement interactive video streaming infrastructure',
    'Develop AI-powered personalized learning paths',
    'Build comprehensive assessment and tracking system',
    'Scale platform to support 100K+ concurrent users'],

    testimonial: {
      quote: 'This platform has democratized education. Students from remote areas now have access to world-class learning resources.',
      clientName: 'Prof. Fatima Hassan',
      clientRole: 'Director, EduTech Initiative',
      clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_15d2ad1ce-1763295118542.png",
      clientImageAlt: 'Professional portrait of African woman professor wearing traditional headwrap and modern blazer with warm scholarly expression'
    },
    metrics: [
    { label: 'Enrolled Students', value: '100K+', percentage: 100 },
    { label: 'Course Completion', value: '78%', percentage: 78 },
    { label: 'Student Satisfaction', value: '4.6/5', percentage: 92 },
    { label: 'Platform Uptime', value: '99.7%', percentage: 99 }],

    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com',
    featured: false
  },
  {
    id: 7,
    title: 'Music Production & Branding',
    category: 'creative',
    thumbnail: "https://images.unsplash.com/photo-1695988549009-b994fa743124",
    thumbnailAlt: 'Professional music studio setup with mixing console, monitors, and digital audio workstation displayed on multiple screens',
    description: 'Complete music production, artist branding, and digital marketing for Ed Pulse creative works',
    fullDescription: `Led the creative direction and production for Ed Pulse music projects, including album production, artist branding, music video direction, and comprehensive digital marketing strategy. The project established Ed Pulse as a recognized name in the African music scene.\n\nThe campaign generated over 5 million streams across platforms, secured radio airplay in 15 countries, and built a social media following of 250K+ engaged fans.`,
    technologies: ['Ableton Live', 'Pro Tools', 'Adobe Premiere', 'After Effects', 'Social Media Marketing'],
    year: '2024',
    duration: '7 months',
    teamSize: '10 members',
    objectives: [
    'Produce high-quality music album with unique sound',
    'Create compelling artist brand identity',
    'Direct and produce professional music videos',
    'Develop comprehensive digital marketing strategy',
    'Build engaged social media community'],

    testimonial: {
      quote: 'Edrian\'s creative vision brought my music to life. The production quality and branding exceeded all expectations.',
      clientName: 'Ed Pulse',
      clientRole: 'Recording Artist',
      clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1d151198a-1763294483440.png",
      clientImageAlt: 'Artistic portrait of young African male musician wearing stylish streetwear with creative lighting and urban background'
    },
    metrics: [
    { label: 'Total Streams', value: '5M+', percentage: 100 },
    { label: 'Radio Airplay', value: '15 Countries', percentage: 90 },
    { label: 'Social Following', value: '250K+', percentage: 100 },
    { label: 'Engagement Rate', value: '12%', percentage: 85 }],

    liveUrl: 'https://example.com',
    githubUrl: 'https://github.com',
    featured: false
  },
  {
    id: 8,
    title: 'Supply Chain Management System',
    category: 'business',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1520cf999-1764675863237.png",
    thumbnailAlt: 'Modern warehouse management dashboard showing inventory tracking, logistics routes, and real-time shipment monitoring on large display',
    description: 'End-to-end supply chain optimization with real-time tracking and inventory management',
    fullDescription: `Designed and implemented a comprehensive supply chain management system that optimizes logistics operations for a major distribution company. The system provides real-time tracking, automated inventory management, and predictive analytics for demand forecasting.\n\nThe solution reduced operational costs by 35%, improved delivery times by 45%, and increased inventory accuracy to 99.5%, transforming the company\'s logistics operations.`,
    technologies: ['Angular', 'Java', 'MySQL', 'Apache Kafka', 'Tableau'],
    year: '2023', duration: '10 months', teamSize: '16 members',
    objectives: [
    'Implement real-time shipment tracking system', 'Automate inventory management and reordering', 'Develop predictive analytics for demand forecasting', 'Optimize delivery routes and logistics operations', 'Integrate with existing ERP systems'],

    testimonial: {
      quote: 'The efficiency gains from this system have been transformative. Our operations are now running smoother than ever.', clientName: 'Ibrahim Musa', clientRole: 'Operations Director, LogiTech Distribution', clientImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1cc72cde4-1763293898558.png", clientImageAlt: 'Professional portrait of African man in business casual attire with warehouse operations visible in background'
    },
    metrics: [
    { label: 'Cost Reduction', value: '35%', percentage: 35 },
    { label: 'Delivery Speed', value: '45%', percentage: 45 },
    { label: 'Inventory Accuracy', value: '99.5%', percentage: 99 },
    { label: 'Customer Satisfaction', value: '4.7/5', percentage: 94 }],

    liveUrl: 'https://example.com', githubUrl: 'https://github.com',
    featured: false
  }];


  useEffect(() => {
    let filtered = projects;

    if (selectedCategory !== 'all') {
      filtered = filtered?.filter((p) => p?.category === selectedCategory);
    }

    if (selectedTech?.length > 0) {
      filtered = filtered?.filter((p) =>
      selectedTech?.some((tech) => p?.technologies?.includes(tech))
      );
    }

    if (sortBy === 'recent') {
      filtered = [...filtered]?.sort((a, b) => b?.year?.localeCompare(a?.year));
    } else if (sortBy === 'alphabetical') {
      filtered = [...filtered]?.sort((a, b) => a?.title?.localeCompare(b?.title));
    }

    setFilteredProjects(filtered);
  }, [selectedCategory, selectedTech, sortBy]);

  const handleTechToggle = (tech) => {
    setSelectedTech((prev) =>
    prev?.includes(tech) ?
    prev?.filter((t) => t !== tech) :
    [...prev, tech]
    );
  };

  const handleReset = () => {
    setSelectedCategory('all');
    setSelectedTech([]);
    setSortBy('recent');
  };

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const featuredProjects = projects?.filter((p) => p?.featured);

  return (
    <>
      <Helmet>
        <title>Project Portfolio - Edrian Bass | IT Engineering & Creative Works</title>
        <meta name="description" content="Explore Edrian Bass's portfolio of tech projects, business initiatives, and creative works. Showcasing expertise in IT engineering, entrepreneurship, and creative direction." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-24 md:pt-28 lg:pt-32 pb-12 md:pb-16 lg:pb-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-8 md:mb-12 lg:mb-16">

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
                Project Portfolio
              </h1>
              <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
                Explore my collection of tech projects, business initiatives, and creative works that demonstrate expertise across multiple domains
              </p>
            </motion.div>

            <ProjectStats />

            <FeaturedCarousel
              projects={featuredProjects}
              onViewDetails={handleViewDetails} />


            <FilterPanel
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              selectedTech={selectedTech}
              onTechChange={handleTechToggle}
              sortBy={sortBy}
              onSortChange={setSortBy}
              onReset={handleReset} />


            <div className="mb-6 md:mb-8">
              <div className="flex items-center justify-between">
                <h2 className="text-xl md:text-2xl font-semibold">
                  {filteredProjects?.length} {filteredProjects?.length === 1 ? 'Project' : 'Projects'} Found
                </h2>
                {(selectedCategory !== 'all' || selectedTech?.length > 0) &&
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReset}
                  iconName="X"
                  iconPosition="left">

                    Clear Filters
                  </Button>
                }
              </div>
            </div>

            {filteredProjects?.length > 0 ?
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8 mb-8 md:mb-12 lg:mb-16">
                {filteredProjects?.map((project) =>
              <ProjectCard
                key={project?.id}
                project={project}
                onViewDetails={handleViewDetails} />

              )}
              </div> :

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-12 md:py-16 lg:py-20">

                <div className="w-20 h-20 md:w-24 md:h-24 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
                  <Icon name="Search" size={40} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="text-xl md:text-2xl font-semibold mb-2">No Projects Found</h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your filters to see more results
                </p>
                <Button
                variant="outline"
                onClick={handleReset}
                iconName="RotateCcw"
                iconPosition="left">

                  Reset Filters
                </Button>
              </motion.div>
            }

            <ClientLogos />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-12">
              <NewsletterSubscription />
              <ConsultationBooking />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-r from-primary to-secondary rounded-xl p-6 md:p-8 lg:p-12 text-center">

              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">
                Have a Project in Mind?
              </h2>
              <p className="text-base md:text-lg text-white/90 mb-6 md:mb-8 max-w-2xl mx-auto">
                Let's collaborate to bring your vision to life. Whether it's tech development, business strategy, or creative direction, I'm here to help.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                <Button
                  variant="default"
                  size="lg"
                  onClick={() => window.location.href = '/contact-hub'}
                  iconName="Send"
                  iconPosition="right"
                  className="bg-white text-primary hover:bg-white/90">

                  Start a Project
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => window.location.href = '/services-portfolio'}
                  iconName="Briefcase"
                  iconPosition="left"
                  className="border-white text-white hover:bg-white/10">

                  View Services
                </Button>
              </div>
            </motion.div>
          </div>
        </main>

        <footer className="bg-card border-t border-border py-6 md:py-8">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground text-center md:text-left">
                &copy; {new Date()?.getFullYear()} Edrian Bass. All rights reserved.
              </p>
              <div className="flex items-center gap-4">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                  <Icon name="Linkedin" size={20} color="currentColor" />
                </a>
                <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                  <Icon name="Github" size={20} color="currentColor" />
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                  <Icon name="Twitter" size={20} color="currentColor" />
                </a>
              </div>
            </div>
          </div>
        </footer>

        <WhatsAppWidget />
        
        <ProjectModal
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)} />

      </div>
    </>);





};

export default ProjectPortfolio;